import { useEffect, useMemo, useState } from 'react';
import { responsiveConfig } from 'shared/constants/responsive';

export type Breakpoints = {
  phone: number; // <= phone => phone
  tablet: number; // < tablet => tablet, >= => desktop
};

export type UseBreakpointOptions = {
  breakpoints?: Partial<Breakpoints> | number; // number kept for backward compatibility (interpreted as tablet)
};

export type UseBreakpointResult = {
  width: number | null;
  height: number | null;
  orientation: 'portrait' | 'landscape';
  isPortrait: boolean;
  isLandscape: boolean;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  // Backward-compat flags (using tablet as previous "mobile" threshold = 834)
  isBelowMobile: boolean;
  isAtLeastDesktop: boolean;
  breakpoints: Breakpoints;
};

/**
 * useBreakpoint
 * SSR-safe viewport size + orientation listener.
 * Defaults: phone=500, tablet=834.
 */
export const useBreakpoint = (options?: UseBreakpointOptions | number): UseBreakpointResult => {
  const isClient = typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';

  // Resolve breakpoints with backward compatibility
  const bp: Breakpoints = useMemo(() => {
    if (typeof options === 'number') {
      return { phone: responsiveConfig.phoneBreakpoint, tablet: options };
    }
    const partial = (options && options.breakpoints) || {};
    const tablet = typeof partial === 'number' ? partial : (partial.tablet ?? responsiveConfig.tabletBreakpoint);
    const phone = typeof partial === 'number' ? responsiveConfig.phoneBreakpoint : (partial.phone ?? responsiveConfig.phoneBreakpoint);
    return { phone, tablet };
  }, [options]);

  const getDims = () => ({
    width: isClient ? window.innerWidth : null,
    height: isClient ? window.innerHeight : null,
  });

  const [{ width, height }, setSize] = useState<{ width: number | null; height: number | null }>(() => getDims());

  useEffect(() => {
    if (!isClient) return;

    const handleResize = () => setSize(getDims());

    // media queries for change events (width/orientation)
    const mqMin = window.matchMedia(`(min-width: ${bp.tablet}px)`);
    const mqOrientation = window.matchMedia('(orientation: portrait)');
    const listener = () => handleResize();

    if (typeof mqMin.addEventListener === 'function') {
      mqMin.addEventListener('change', listener);
    } else if (typeof mqMin.addListener === 'function') {
      mqMin.addListener(listener);
    }

    if (typeof mqOrientation.addEventListener === 'function') {
      mqOrientation.addEventListener('change', listener);
    } else if (typeof mqOrientation.addListener === 'function') {
      mqOrientation.addListener(listener);
    }

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize as EventListener);

    // Initial sync in case of hydration mismatches
    handleResize();

    return () => {
      if (typeof mqMin.removeEventListener === 'function') {
        mqMin.removeEventListener('change', listener);
      } else if (typeof mqMin.removeListener === 'function') {
        mqMin.removeListener(listener);
      }
      if (typeof mqOrientation.removeEventListener === 'function') {
        mqOrientation.removeEventListener('change', listener);
      } else if (typeof mqOrientation.removeListener === 'function') {
        mqOrientation.removeListener(listener);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize as EventListener);
    };
  }, [isClient, bp.tablet]);

  const orientation = useMemo<'portrait' | 'landscape'>(() => {
    if (width == null || height == null) return 'landscape';
    return height >= width ? 'portrait' : 'landscape';
  }, [width, height]);

  const isPortrait = orientation === 'portrait';
  const isLandscape = orientation === 'landscape';

  const isPhone = useMemo(() => (width !== null ? width <= bp.phone : false), [width, bp.phone]);
  const isDesktop = useMemo(() => (width !== null ? width >= bp.tablet : true), [width, bp.tablet]);
  const isTablet = useMemo(() => (width !== null ? width > bp.phone && width < bp.tablet : false), [width, bp]);

  // Back-compat flags (older code treated "mobile" as < 834)
  const isBelowMobile = useMemo(() => (width !== null ? width < bp.tablet : false), [width, bp.tablet]);
  const isAtLeastDesktop = isDesktop;

  return {
    width,
    height,
    orientation,
    isPortrait,
    isLandscape,
    isPhone,
    isTablet,
    isDesktop,
    isBelowMobile,
    isAtLeastDesktop,
    breakpoints: bp,
  };
};
